/**
 * SmartBackupsAdminPanel — the content of the "גיבויים" tab inside the
 * super-admin Work&Credits modal.
 *
 * Three sections, top → bottom:
 *   1. Google Drive folder configuration  (so admin can paste the URL
 *      and every future backup auto-uploads there).
 *   2. Big "צור גיבוי עכשיו" CTA  +  "פתח לוח שנה" link.
 *   3. Full list of all snapshots (newest first), each row showing
 *      date+time, size, Drive link (or warning), and per-row actions
 *      (download / restore / delete).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Cloud, ExternalLink, ShieldCheck, CheckCircle2, Plus, RefreshCw,
  Download, RotateCcw, Calendar, AlertTriangle, Save, Trash2,
} from "lucide-react";
import { api } from "../lib/api";
import SmartBackupCalendar from "./SmartBackupCalendar";

function fmtSize(n) {
  if (!n) return "0KB";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("he-IL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export default function SmartBackupsAdminPanel({ companyId }) {
  // === Drive folder config ===
  const [folderUrl, setFolderUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [driveStatus, setDriveStatus] = useState({ configured: false, connected: false });

  // === Snapshots list ===
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [restoring, setRestoring] = useState({});
  const [deleting, setDeleting] = useState({});

  const loadAll = async () => {
    setLoading(true);
    try {
      const [companiesRes, snapsRes, infoRes] = await Promise.all([
        api.get("/companies"),
        api.get(`/companies/${companyId}/smart-snapshots?days=60&include_deleted=true`),
        api.get("/gdrive/info").catch(() => ({ data: { configured: false, connected: false } })),
      ]);
      const items = Array.isArray(companiesRes.data)
        ? companiesRes.data
        : (companiesRes.data.items || companiesRes.data.companies || []);
      const comp = items.find((c) => c.id === companyId) || {};
      const url = comp.gdrive_folder_url || "";
      setFolderUrl(url);
      setSavedUrl(url);
      setSnapshots(snapsRes.data?.snapshots || []);
      setDriveStatus({
        configured: !!infoRes.data?.configured,
        connected: !!infoRes.data?.connected,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "טעינה נכשלה");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [companyId]);

  // === Group snapshots by date for the "all backups by day" display ===
  const byDate = useMemo(() => {
    const m = {};
    for (const s of snapshots) {
      const d = s.date || (s.created_at || "").slice(0, 10);
      if (!m[d]) m[d] = [];
      m[d].push(s);
    }
    // sort each day's bucket by time desc
    for (const d of Object.keys(m)) {
      m[d].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return m;
  }, [snapshots]);
  const sortedDates = useMemo(() => Object.keys(byDate).sort((a, b) => b.localeCompare(a)), [byDate]);

  // === Drive folder save ===
  const onSaveFolder = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}`, { gdrive_folder_url: folderUrl.trim() });
      setSavedUrl(folderUrl.trim());
      toast.success(folderUrl.trim() ? "תיקיית Drive נשמרה" : "הקישור נמחק");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירה נכשלה");
    } finally { setSaving(false); }
  };
  const onVerifyFolder = async () => {
    if (!folderUrl.trim()) { toast.error("הדבק קישור תיקייה תחילה"); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/gdrive/verify", { folder_url: folderUrl });
      setVerifyResult(data);
      if (data.ok) toast.success(`חיבור תקין: ${data.folder_name}`);
      else toast.error(data.error || "בדיקה נכשלה");
    } catch (e) {
      toast.error(e.response?.data?.detail || "בדיקה נכשלה");
    } finally { setVerifying(false); }
  };
  const onConnectDrive = async () => {
    try {
      const { data } = await api.get("/gdrive/oauth/start");
      const popup = window.open(data.url, "drive_oauth", "width=520,height=680");
      const t = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(t);
          await loadAll();
          toast.success("Drive חובר בהצלחה");
        }
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "חיבור Drive נכשל");
    }
  };

  // === Manual snapshot ===
  const onCreateNow = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/smart-snapshots/create-now`);
      const snap = data?.snapshot || {};
      if (data?.created) {
        if (snap.gdrive_link) {
          toast.success("גיבוי נוצר והועלה ל-Drive ✓", {
            description: snap.gdrive_link, duration: 6000,
          });
        } else if (snap.gdrive_error === "no_gdrive_folder_configured") {
          toast.warning("גיבוי נוצר — תיקיית Drive לא מוגדרת", {
            description: "הדבק URL של תיקייה למעלה כדי שהגיבויים יעלו אוטומטית",
            duration: 8000,
          });
        } else if (snap.gdrive_error) {
          toast.warning("גיבוי נוצר — Drive נכשל", {
            description: snap.gdrive_error.slice(0, 120),
          });
        } else {
          toast.success("גיבוי נוצר בהצלחה");
        }
      } else {
        toast(data?.reason || "לא נוצר", { icon: "ℹ️" });
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "יצירה נכשלה");
    } finally { setCreating(false); }
  };

  const onRestoreSnap = async (snap) => {
    if (!window.confirm(`לשחזר מ-${fmtDateTime(snap.created_at)}?\nהמצב הנוכחי יישמר אוטומטית כגיבוי בטיחות.`)) return;
    setRestoring((s) => ({ ...s, [snap.id]: true }));
    try {
      await api.post(`/companies/${companyId}/smart-snapshots/${snap.id}/restore`);
      toast.success("שוחזר בהצלחה — דף ייטען מחדש");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שחזור נכשל");
    } finally { setRestoring((s) => ({ ...s, [snap.id]: false })); }
  };

  const onDeleteSnap = async (snap) => {
    if (!window.confirm(`למחוק את הגיבוי מ-${fmtDateTime(snap.created_at)}?\nהפעולה לא ניתנת לביטול.`)) return;
    setDeleting((s) => ({ ...s, [snap.id]: true }));
    try {
      await api.delete(`/companies/${companyId}/smart-snapshots/${snap.id}`);
      toast.success("הגיבוי נמחק");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "מחיקה נכשלה");
    } finally { setDeleting((s) => ({ ...s, [snap.id]: false })); }
  };

  return (
    <div className="space-y-4" data-testid="smart-backups-admin-panel">
      {/* === 1. Google Drive folder configuration === */}
      <section className="rounded-xl border border-sky-500/30 bg-gradient-to-br from-sky-950/40 to-stone-900 p-4" data-testid="gdrive-config-section">
        <div className="flex items-center gap-2 mb-3">
          <Cloud className="h-4 w-4 text-sky-300" />
          <h4 className="text-sm font-extrabold text-white">סנכרון Google Drive</h4>
          {savedUrl && (
            <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-2 py-0.5">
              פעיל
            </span>
          )}
        </div>

        <p className="text-[11px] text-stone-400 mb-2 leading-snug">
          הדבק כאן URL של תיקיית Drive — כל גיבוי (יומי או ידני) יעלה אוטומטית לתיקייה הזו.
        </p>

        <input
          type="text"
          value={folderUrl}
          onChange={(e) => { setFolderUrl(e.target.value); setVerifyResult(null); }}
          placeholder="https://drive.google.com/drive/folders/..."
          dir="ltr"
          className="w-full text-right bg-stone-900 border border-white/10 rounded-lg px-3 py-2 text-sky-100 placeholder:text-stone-600 text-[12px] focus:outline-none focus:border-sky-500/60"
          data-testid="admin-gdrive-folder-url"
        />

        <div className="flex gap-1.5 mt-2">
          <button
            type="button"
            onClick={onVerifyFolder}
            disabled={verifying || !folderUrl.trim() || !driveStatus.connected}
            title={!driveStatus.connected ? "חבר את Drive תחילה" : ""}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-stone-700 hover:bg-stone-600 text-stone-100 font-bold text-[12px] disabled:opacity-50"
            data-testid="admin-gdrive-verify"
          >
            {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            בדוק חיבור
          </button>
          <button
            type="button"
            onClick={onSaveFolder}
            disabled={saving || folderUrl.trim() === savedUrl.trim()}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-[12px] disabled:opacity-50"
            data-testid="admin-gdrive-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            שמור
          </button>
        </div>

        {verifyResult && (
          <div className={`mt-2 text-[11px] rounded-lg px-2 py-1 ${verifyResult.ok ? "bg-emerald-500/15 text-emerald-200" : "bg-red-500/15 text-red-200"}`}>
            {verifyResult.ok
              ? <span>✓ {verifyResult.folder_name}</span>
              : <span>✗ {verifyResult.error}</span>}
          </div>
        )}

        {savedUrl && (
          <a href={savedUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] text-sky-300 hover:text-sky-200">
            <ExternalLink className="h-3 w-3" /> פתח תיקייה
          </a>
        )}

        {/* OAuth connect — only when Drive integration is configured */}
        {driveStatus.configured && !driveStatus.connected && (
          <div className="mt-3 space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
            <p className="text-[11px] text-amber-200 leading-snug">
              ⚠️ Drive מוגדר אבל עוד לא חובר בחשבון שלך. חבר עכשיו כדי שגיבויים יעלו.
            </p>
            <button
              type="button"
              onClick={onConnectDrive}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-[12px]"
              data-testid="admin-gdrive-connect"
            >
              <Cloud className="h-3.5 w-3.5" /> חבר Drive
            </button>
          </div>
        )}
      </section>

      {/* === 2. Create-now CTA + calendar link === */}
      <section className="flex items-center gap-2" data-testid="snapshot-actions">
        <button
          type="button"
          onClick={onCreateNow}
          disabled={creating}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-l from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-extrabold text-sm shadow-lg shadow-emerald-900/30 disabled:opacity-50"
          data-testid="admin-snapshot-create-now"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {creating ? "מגבה ומעלה ל-Drive..." : "צור גיבוי עכשיו"}
        </button>
        <button
          type="button"
          onClick={() => setCalendarOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-200 font-bold text-[12px]"
          data-testid="admin-open-calendar"
          title="לוח שנה של גיבויים"
        >
          <Calendar className="w-4 h-4" /> לוח שנה
        </button>
      </section>

      {/* === 3. All backups list, grouped by day === */}
      <section className="rounded-xl border border-stone-800 bg-stone-950/60" data-testid="snapshots-list-section">
        <header className="px-4 py-2 border-b border-stone-800 flex items-center justify-between">
          <h4 className="text-sm font-extrabold text-white inline-flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5 text-fuchsia-300" />
            כל הגיבויים
          </h4>
          <span className="text-[11px] text-stone-400 tabular-nums">
            {snapshots.length} סך הכל · 60 ימים אחרונים
          </span>
        </header>

        {loading ? (
          <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-stone-400" /></div>
        ) : sortedDates.length === 0 ? (
          <div className="p-6 text-center text-stone-400 text-sm">
            עדיין אין גיבויים. לחץ "צור גיבוי עכשיו" כדי להתחיל.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto divide-y divide-stone-800">
            {sortedDates.map((date) => {
              const dayBucket = byDate[date];
              return (
                <div key={date} data-testid={`snap-day-${date}`}>
                  <div className="px-4 py-1.5 bg-stone-900/80 text-[11px] font-extrabold text-stone-300 sticky top-0 z-10 flex items-center justify-between">
                    <span>{date}</span>
                    <span className="text-stone-500 font-normal">{dayBucket.length} גיבויים</span>
                  </div>
                  {dayBucket.map((s) => {
                    const isDeleted = !!s.deleted_at;
                    const deletedSize = isDeleted ? (s.file_size_at_deletion || 0) : s.file_size;
                    const deletedByLabel =
                      s.deleted_by_role === "super_admin" ? "נמחק על ידי האדמין" :
                      s.deleted_by_role ? "נמחק על ידי הלקוח" :
                      "נמחק";
                    return (
                    <div
                      key={s.id}
                      className={"px-4 py-2 transition flex items-center gap-3 " +
                        (isDeleted
                          ? "opacity-50 line-through bg-stone-950/40"
                          : "hover:bg-stone-900/50")}
                      data-testid={`snap-row-${s.id}`}
                    >
                      <div className="flex-shrink-0 w-14 text-[11px] text-stone-300 font-bold tabular-nums">
                        {new Date(s.created_at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                            s.trigger === "manual" ? "bg-fuchsia-500/20 text-fuchsia-200" :
                            s.trigger === "pre_restore" ? "bg-amber-500/20 text-amber-200" :
                            "bg-emerald-500/20 text-emerald-200"
                          }`}>
                            {s.trigger === "manual" ? "ידני" : s.trigger === "pre_restore" ? "אוטומטי" : "אוטומטי"}
                          </span>
                          <span className="text-stone-400">{fmtSize(deletedSize)}</span>
                          {!isDeleted && s.gdrive_link ? (
                            <a href={s.gdrive_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-sky-300 hover:text-sky-200" title="פתח ב-Drive">
                              <Cloud className="h-2.5 w-2.5" /> Drive
                            </a>
                          ) : !isDeleted && s.gdrive_error === "no_gdrive_folder_configured" ? (
                            <span className="text-amber-300/80 text-[10px]" title="תיקיית Drive לא מוגדרת">⚠ אין Drive</span>
                          ) : !isDeleted && s.gdrive_error && s.gdrive_error !== "imported_from_upload" ? (
                            <span className="text-red-300/80 text-[10px]" title={s.gdrive_error}>⚠ שגיאת Drive</span>
                          ) : null}
                          {isDeleted && (
                            <span className="no-underline text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5 text-[9px] font-bold" title={s.deleted_at}>
                              {deletedByLabel} · {new Date(s.deleted_at).toLocaleString("he-IL", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}
                            </span>
                          )}
                        </div>
                        {isDeleted && s.deleted_by_email && (
                          <div className="no-underline text-[10px] text-amber-300/70 mt-0.5">
                            {s.deleted_by_email}
                          </div>
                        )}
                      </div>
                      {!isDeleted && (
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/smart-snapshots/${s.id}/download`}
                            download
                            className="p-1.5 rounded-md text-sky-300 hover:bg-sky-500/20 transition"
                            data-testid={`snap-download-${s.id}`}
                            title="הורד ZIP"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                          <button
                            type="button"
                            onClick={() => onRestoreSnap(s)}
                            disabled={!!restoring[s.id]}
                            className="p-1.5 rounded-md text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
                            data-testid={`snap-restore-${s.id}`}
                            title="שחזר מהגיבוי הזה"
                          >
                            {restoring[s.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteSnap(s)}
                            disabled={!!deleting[s.id]}
                            className="p-1.5 rounded-md text-red-300 hover:bg-red-500/20 transition disabled:opacity-50"
                          data-testid={`snap-delete-${s.id}`}
                          title="מחק את הגיבוי"
                        >
                          {deleting[s.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {!driveStatus.configured && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 p-2 text-[11px] leading-snug flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <div>
            אינטגרציית Drive לא מוגדרה ברמת המערכת (חסרים <code>GOOGLE_CLIENT_ID</code> /
            <code>GOOGLE_CLIENT_SECRET</code> ב-env). הגיבויים נשמרים מקומית בלבד.
          </div>
        </div>
      )}

      {/* Calendar modal */}
      {calendarOpen && (
        <SmartBackupCalendar
          companyId={companyId}
          onClose={() => { setCalendarOpen(false); loadAll(); }}
        />
      )}
    </div>
  );
}
